import { Edge, MarkerType } from '@xyflow/react';
import { ConfigurationMapEdge } from '@rossum/api-client/internal';

type EdgeColors = {
  default: string;
  highlighted: string;
  external: string;
  runAfter: string;
  cycle: string;
};

type EdgeKind = {
  isCycle: boolean;
  isHighlighted: boolean;
  isExternal: boolean;
  isRunAfter: boolean;
};

const pickColor = (kind: EdgeKind, colors: EdgeColors): string => {
  if (kind.isCycle) return colors.cycle;
  if (!kind.isHighlighted) return colors.default;
  if (kind.isRunAfter) return colors.runAfter;
  if (kind.isExternal) return colors.external;
  return colors.highlighted;
};

/**
 * Maps raw configuration-map edges to ReactFlow `Edge` objects with
 * selection-aware styling (highlighting, hiding, external coloring).
 * Hook→hook edges (run-after) use a dashed stroke. Edges on a problematic
 * circular dependency are always visible and error-colored — they mark a
 * broken setup, so they must not fade when an unrelated node is selected.
 */
export const computeDisplayEdges = (
  edges: ConfigurationMapEdge[],
  selectedNodeId: string | null,
  connectedEdges: Set<string>,
  externalFieldIds: Set<string>,
  hookIds: Set<string>,
  cycleEdgeKeys: Set<string>,
  colors: EdgeColors
): Edge[] =>
  edges.map(edge => {
    const edgeKey = `${edge.source}-${edge.target}`;
    const kind: EdgeKind = {
      isCycle: cycleEdgeKeys.has(edgeKey),
      isHighlighted: selectedNodeId !== null && connectedEdges.has(edgeKey),
      isExternal:
        externalFieldIds.has(edge.source) || externalFieldIds.has(edge.target),
      isRunAfter: hookIds.has(edge.source) && hookIds.has(edge.target),
    };
    const edgeColor = pickColor(kind, colors);

    return {
      id: edgeKey,
      source: edge.source,
      target: edge.target,
      type: 'default',
      hidden: selectedNodeId !== null && !kind.isHighlighted && !kind.isCycle,
      interactionWidth: 0,
      style: {
        stroke: edgeColor,
        strokeWidth: kind.isCycle ? 1.5 : 1,
        strokeDasharray: kind.isCycle
          ? '6 3'
          : kind.isRunAfter
            ? '4 3'
            : undefined,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: edgeColor,
        width: 12,
        height: 12,
      },
    };
  });
